import React from "react";
import { ExternalLink, PenLine, Tv } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { BOOK, SHOW, PHOTOS } from "@/lib/data";

const BookHost = () => (
  <section id="book" className="relative py-16 sm:py-24 bg-[#0E142B]/60 border-y border-[#D4AF37]/10">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="grid lg:grid-cols-2 gap-14 lg:gap-16 items-center">
        {/* Book with real cover */}
        <Reveal className="relative order-2 lg:order-1">
          <div className="relative rounded-[28px] border border-[#D4AF37]/20 bg-gradient-to-br from-[#141d40] to-[#0B1021] p-8 sm:p-12">
            <div className="flex flex-col sm:flex-row gap-9 items-center sm:items-start">
              <div className="shrink-0 relative group" style={{ perspective: 800 }}>
                <img
                  src={PHOTOS.bookCover}
                  alt="Infinite Life, Infinite Lessons — the international bestseller by Susan Grau, published by Hay House"
                  className="w-44 sm:w-48 aspect-[2/3] object-cover rounded-r-lg rounded-l-sm shadow-[0_30px_80px_rgba(0,0,0,0.75),0_0_50px_rgba(212,175,55,0.15)] border-l-4 border-[#D4AF37]/60 transition-transform duration-500 group-hover:scale-[1.04] group-hover:-rotate-1"
                />
                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-[#0B1021] border border-[#D4AF37]/40 rounded-full px-4 py-1.5 w-max">
                  <span className="text-[9px] tracking-[0.22em] uppercase text-[#F3D068]">
                    International Bestseller
                  </span>
                </div>
              </div>
              <div className="text-center sm:text-left">
                <p className="eyebrow">The Bestselling Book</p>
                <h3 className="font-display text-2xl sm:text-3xl text-[#FDFBF7] mt-4 leading-snug">
                  {BOOK.fullTitle}
                </h3>
                <p className="text-sm text-[#9CA3AF] font-light mt-4 leading-relaxed">
                  The story of the freezer, the angels, and the Well of Knowledge — over 10,000
                  copies sold, with international editions in Turkey and Spain. Published by Hay
                  House. This is the book that made Susan a household name in evidential
                  mediumship.
                </p>
                <div className="flex flex-wrap justify-center sm:justify-start gap-3 mt-7">
                  <a
                    href={BOOK.amazonUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="book-buy-amazon-button"
                    className="btn-gold rounded-full px-6 py-3 text-sm font-bold inline-flex items-center gap-2"
                  >
                    Buy on Amazon <ExternalLink size={14} />
                  </a>
                  <button
                    data-testid="book-signed-addon-cta"
                    onClick={() => document.getElementById("seating")?.scrollIntoView({ behavior: "smooth" })}
                    className="btn-ghost-gold rounded-full px-6 py-3 text-sm font-medium inline-flex items-center gap-2"
                  >
                    <PenLine size={14} /> Signed copy at checkout — $35
                  </button>
                </div>
                <p className="text-xs text-[#8A99B5] mt-5 italic">
                  Coming 2027: When Shadows Speak, with a foreword by Valerie Bertinelli.
                </p>
              </div>
            </div>
          </div>
        </Reveal>

        {/* The Show */}
        <div className="order-1 lg:order-2">
          <SectionHeading
            align="left"
            eyebrow="Beyond the Stage"
            title={
              <>
                Host of <em className="text-gold-gradient italic">A View from the Top</em> with Susan Grau
              </>
            }
            sub="Her show brings conversations at the edge of the veil — with the mediums, healers, and souls who live between the two worlds. This New Year, it steps out of the studio and onto the Saban stage."
          />
          <Reveal delay={0.15}>
            <div className="mt-9 rounded-[24px] overflow-hidden gold-ring bg-[#131B36]">
              <img
                src={PHOTOS.studioOverShoulder}
                alt="Susan Grau on the A View from the Top set at CBS Television City"
                className="w-full h-auto object-cover"
              />
              <div className="p-7 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-5">
                <div className="text-center sm:text-left">
                  <p className="text-[10px] uppercase tracking-[0.28em] text-[#F3D068]">On set · CBS Television City</p>
                  <p className="text-sm text-[#9CA3AF] font-light leading-relaxed mt-2">
                    The show tapes here all season — and this New Year, it steps onto the Saban stage.
                  </p>
                </div>
                <a
                  href={SHOW.studioTicketsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="host-studio-tickets"
                  className="btn-ghost-gold rounded-full px-5 py-2.5 text-xs font-medium inline-flex items-center gap-1.5 shrink-0"
                >
                  <Tv size={13} /> Studio Audience Tickets
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

export default BookHost;
